import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getToken } from "./tokenStorage";
import { AuthCtx } from "./AuthProvider";
import { fetchJsonCached } from "./apiCache";

const API = window.location.origin;

function getLessonTypeClass(type, selected) {
  const key = (type || '').toLowerCase();
  if (!selected) return 'bg-[#f2f2f7] text-[#1c1c1e]';
  if (key.includes('лек')) return 'bg-amber-100 text-amber-700 border border-amber-300 shadow-[0_4px_12px_-6px_rgba(245,158,11,0.5)]';
  if (key.includes('прак') || key.includes('семин')) return 'bg-emerald-100 text-emerald-700 border border-emerald-300 shadow-[0_4px_12px_-6px_rgba(16,185,129,0.5)]';
  if (key.includes('лаб')) return 'bg-violet-100 text-violet-700 border border-violet-300 shadow-[0_4px_12px_-6px_rgba(139,92,246,0.5)]';
  if (key.includes('урок')) return 'bg-sky-100 text-sky-700 border border-sky-300 shadow-[0_4px_12px_-6px_rgba(14,165,233,0.4)]';
  return 'bg-[#1c1c1e] text-white';
}

const SOURCE_CODES = ["СПО", "ВО"];
const ARCHIVE_SOURCE_CODES = ["СПО", "ВО", "ОЗО"];
const ARCHIVE_IMPORT_JOB_KEY = "audassist.archiveImportJob";
const ARCHIVE_IMPORT_JOB_SNAPSHOT_KEY = "audassist.archiveImportJobSnapshot";

function mergeFileLists(existing, incoming) {
  const bucket = new Map();
  [...(Array.isArray(existing) ? existing : []), ...(Array.isArray(incoming) ? incoming : [])].forEach((file) => {
    if (!file) return;
    const key = `${file.name || ""}::${file.size || 0}::${file.lastModified || 0}`;
    if (!bucket.has(key)) {
      bucket.set(key, file);
    }
  });
  return Array.from(bucket.values());
}

function formatFileSize(size) {
  const value = Number(size || 0);
  if (!Number.isFinite(value) || value <= 0) return "0 Б";
  if (value >= 1024 * 1024) {
    return `${(value / 1024 / 1024).toFixed(value >= 10 * 1024 * 1024 ? 0 : 1)} МБ`;
  }
  if (value >= 1024) {
    return `${Math.round(value / 1024)} КБ`;
  }
  return `${Math.round(value)} Б`;
}

function formatArchiveUploadedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const label = date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return sameDay ? `${label} · сохранен сегодня` : label;
}

function isArchiveDuplicateError(message, logs = []) {
  const haystack = [message, ...(Array.isArray(logs) ? logs : [])].join(" ").toLowerCase();
  return (
    haystack.includes("уже загружал") ||
    haystack.includes("уже добавлен в эту загрузку") ||
    haystack.includes("дубль")
  );
}

function normalizeArchiveUploadStatus(value) {
  const status = String(value || "").trim().toLowerCase();
  return ["queued", "running", "success", "error", "unknown"].includes(status) ? status : "unknown";
}

function archiveUploadBlocksDuplicate(item) {
  return normalizeArchiveUploadStatus(item?.status) === "success";
}

async function computeFileSha256(file) {
  if (!file || !window.crypto?.subtle) return "";
  const buffer = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function ExcelFileIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className={className}>
      <path fill="#F8FAFC" d="M14 4h14.5L40 15.5V38a6 6 0 0 1-6 6H14a6 6 0 0 1-6-6V10a6 6 0 0 1 6-6Z" />
      <path fill="#E2E8F0" d="M28 4v8.5a3 3 0 0 0 3 3H40L28 4Z" />
      <rect x="6.5" y="13" width="18" height="22" rx="5" fill="#107C41" />
      <path fill="#fff" d="m13.2 29.3 2.7-4.2-2.5-3.8h2.4l1.4 2.4 1.4-2.4h2.3l-2.5 3.8 2.7 4.2H18.7l-1.6-2.8-1.6 2.8h-2.3Z" />
      <rect x="27" y="23" width="8.5" height="1.9" rx=".95" fill="#94A3B8" />
      <rect x="27" y="27" width="6.5" height="1.9" rx=".95" fill="#94A3B8" />
    </svg>
  );
}

function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function parseIsoDateLocal(iso, endOfDay = false) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(
    y,
    m - 1,
    d,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0
  );
  if (Number.isNaN(date.getTime())) return null;
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return null;
  }
  return date;
}

function parseWeekStart(weekStr) {
  // weekStr: "YYYY-MM-DD_YYYY-MM-DD"
  try {
    const start = weekStr.split("_")[0];
    return parseIsoDateLocal(start, false);
  } catch {
    return null;
  }
}

function parseWeekEnd(weekStr) {
  try {
    const end = weekStr.split("_")[1];
    return parseIsoDateLocal(end, true);
  } catch {
    return null;
  }
}

function formatDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const CALENDAR_WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function formatPrettyDate(iso) {
  const date = parseIsoDateLocal(iso, false);
  if (!date) return "";
  return date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function toMonthKey(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 7);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
}

function monthDateFromKey(key) {
  if (!/^\d{4}-\d{2}$/.test(String(key || ""))) return null;
  return parseIsoDateLocal(`${key}-01`, false);
}

function buildCalendarMonthCells(monthDate) {
  if (!monthDate) return [];
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = (firstDay.getDay() + 6) % 7;
  const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;
  const cells = [];

  for (let index = 0; index < totalCells; index += 1) {
    const dayNumber = index - offset + 1;
    if (dayNumber < 1 || dayNumber > daysInMonth) {
      cells.push(null);
      continue;
    }
    const date = new Date(year, month, dayNumber);
    cells.push({
      iso: formatDateInputValue(date),
      dayNumber,
    });
  }

  return cells;
}

function normalizeWeekId(value) {
  return String(value || "").trim().split("__", 1)[0];
}

function normalizeWeeksList(values) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => normalizeWeekId(value))
        .filter(Boolean)
    )
  );
}

export default function TeacherExport() {
  const { user } = useContext(AuthCtx);
  const [weeks, setWeeks] = useState([]);
  const [selectedSource, setSelectedSource] = useState(SOURCE_CODES[0]);
  const [dateFrom, setDateFrom] = useState(""); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState("");
  const [calendarMonthKey, setCalendarMonthKey] = useState("");
  const [calendarDirection, setCalendarDirection] = useState(1);

  const [teacherQuery, setTeacherQuery] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [showTeacherSuggestions, setShowTeacherSuggestions] = useState(false);
  const teacherBlurTimer = useRef(null);

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [groupFilter, setGroupFilter] = useState([]);
  const [disciplineFilter, setDisciplineFilter] = useState([]);

  const [groups, setGroups] = useState([]);
  const [disciplines, setDisciplines] = useState([]);

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [selectedLessonTypes, setSelectedLessonTypes] = useState([]);
  const [archiveMenuOpen, setArchiveMenuOpen] = useState(false);
  const [archiveFiles, setArchiveFiles] = useState([]);
  const [archiveSource, setArchiveSource] = useState(SOURCE_CODES[0]);
  const [archiveUploading, setArchiveUploading] = useState(false);
  const [archiveMsg, setArchiveMsg] = useState("");
  const [archiveLogs, setArchiveLogs] = useState([]);
  const [archiveUploadItems, setArchiveUploadItems] = useState([]);
  const [archiveUploadDots, setArchiveUploadDots] = useState(".");
  const [archiveDragActive, setArchiveDragActive] = useState(false);
  const [archiveUploadsExpanded, setArchiveUploadsExpanded] = useState(false);
  const [archiveOverlayNotice, setArchiveOverlayNotice] = useState(null);
  const [teacherLoadMenuOpen, setTeacherLoadMenuOpen] = useState(false);
  const [teacherLoadFiles, setTeacherLoadFiles] = useState([]);
  const [teacherLoadCollectionName, setTeacherLoadCollectionName] = useState("");
  const [teacherLoadUploading, setTeacherLoadUploading] = useState(false);
  const [teacherLoadMsg, setTeacherLoadMsg] = useState("");
  const [teacherLoadItems, setTeacherLoadItems] = useState([]);
  const archiveMenuRef = useRef(null);
  const archiveInputRef = useRef(null);
  const teacherLoadMenuRef = useRef(null);
  const teacherLoadInputRef = useRef(null);
  const archiveOverlayTimersRef = useRef([]);
  const archiveFileHashesRef = useRef(new WeakMap());

  const normalize = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");
  const canManageParser = user?.role === "admin" || user?.role === "support";

  const addTeacherSelection = useCallback((value) => {
    const teacherName = (value || "").trim();
    if (!teacherName) return false;

    const canonical = teachers.find((item) => normalize(item) === normalize(teacherName)) || teacherName;
    let changed = false;
    setSelectedTeacher((prev) => {
      if (normalize(prev) === normalize(canonical)) {
        return prev;
      }
      changed = true;
      return canonical;
    });
    setTeacherQuery("");
    setShowTeacherSuggestions(false);
    return changed;
  }, [teachers]);

  const showArchiveOverlayNotice = useCallback((text, tone = "warning") => {
    archiveOverlayTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    archiveOverlayTimersRef.current = [];
    const noticeId = `${Date.now()}-${Math.random()}`;
    setArchiveOverlayNotice({ id: noticeId, text, tone, visible: true });
    archiveOverlayTimersRef.current.push(
      window.setTimeout(() => {
        setArchiveOverlayNotice((prev) => (
          prev && prev.id === noticeId ? { ...prev, visible: false } : prev
        ));
      }, 2600)
    );
    archiveOverlayTimersRef.current.push(
      window.setTimeout(() => {
        setArchiveOverlayNotice((prev) => (prev && prev.id === noticeId ? null : prev));
      }, 3200)
    );
  }, []);

  const processArchiveCandidateFiles = useCallback(async (incomingFiles) => {
    const candidates = Array.isArray(incomingFiles) ? incomingFiles : [];
    if (!candidates.length) return;

    setArchiveMsg("");
    setArchiveLogs([]);

    const uploadedItemsForSource = archiveUploadItems.filter(
      (item) => (!archiveSource || item?.source === archiveSource) && archiveUploadBlocksDuplicate(item)
    );
    const uploadedHashes = new Set(
      uploadedItemsForSource
        .map((item) => String(item?.content_hash || "").trim().toLowerCase())
        .filter(Boolean)
    );
    const uploadedFallbackKeys = new Set(
      uploadedItemsForSource.map((item) =>
        `${String(item?.original_name || item?.saved_name || "").trim().toLowerCase()}::${Number(item?.size || 0)}`
      )
    );

    const selectedHashes = new Set(
      archiveFiles
        .map((file) => archiveFileHashesRef.current.get(file))
        .filter(Boolean)
    );
    const selectedFallbackKeys = new Set(
      archiveFiles.map((file) => `${String(file?.name || "").trim().toLowerCase()}::${Number(file?.size || 0)}`)
    );

    const acceptedFiles = [];

    for (const file of candidates) {
      const fallbackKey = `${String(file?.name || "").trim().toLowerCase()}::${Number(file?.size || 0)}`;
      let hash = "";
      try {
        hash = await computeFileSha256(file);
      } catch {
        hash = "";
      }

      const uploadedDuplicate = hash ? uploadedHashes.has(hash) : uploadedFallbackKeys.has(fallbackKey);
      if (uploadedDuplicate) {
        showArchiveOverlayNotice(`Этот архив уже загружался для источника ${archiveSource}`, "warning");
        continue;
      }

      const selectedDuplicate = hash ? selectedHashes.has(hash) : selectedFallbackKeys.has(fallbackKey);
      if (selectedDuplicate) {
        showArchiveOverlayNotice("Этот файл уже добавлен в текущий список", "warning");
        continue;
      }

      if (hash) {
        archiveFileHashesRef.current.set(file, hash);
        selectedHashes.add(hash);
      }
      selectedFallbackKeys.add(fallbackKey);
      acceptedFiles.push(file);
    }

    if (acceptedFiles.length) {
      setArchiveFiles((prev) => mergeFileLists(prev, acceptedFiles));
    }
  }, [archiveFiles, archiveSource, archiveUploadItems, showArchiveOverlayNotice]);

  const clearTeacherSelection = useCallback(() => {
    setSelectedTeacher("");
    setTeacherQuery("");
    setShowTeacherSuggestions(false);
  }, []);

  const appendTeacherParams = useCallback((params) => {
    if (selectedTeacher) {
      params.set("teacher", selectedTeacher);
    }
  }, [selectedTeacher]);

  const loadTeacherLoadFiles = useCallback(async () => {
    if (!canManageParser) return;
    try {
      const data = await fetchJsonCached(`${API}/teacher-load/files`, {
        cacheKey: "teacher-load:files",
        ttlMs: 5 * 60_000,
        headers: getAuthHeaders(),
      });
      setTeacherLoadItems(Array.isArray(data?.files) ? data.files : []);
    } catch {
      setTeacherLoadItems([]);
    }
  }, [canManageParser]);

  const loadArchiveUploadFiles = useCallback(async () => {
    if (!canManageParser) return;
    try {
      const data = await fetchJsonCached(`${API}/schedule/archive-upload/files`, {
        cacheKey: "archive-upload:files",
        ttlMs: 5 * 60_000,
        headers: getAuthHeaders(),
      });
      setArchiveUploadItems(Array.isArray(data?.files) ? data.files : []);
    } catch {
      setArchiveUploadItems([]);
    }
  }, [canManageParser]);

  /* --------- загрузка недель --------- */
  const loadWeeks = useCallback(async () => {
    const params = new URLSearchParams();
    params.set("source", selectedSource);
    try {
      const data = await fetchJsonCached(`${API}/export/weeks?${params.toString()}`, {
        cacheKey: `export:weeks:${selectedSource}`,
        ttlMs: 5 * 60_000,
        swr: true,
      });
      setWeeks(normalizeWeeksList(data));
    } catch {
      setWeeks([]);
    }
    window.dispatchEvent(new CustomEvent("pageContentReady"));
  }, [selectedSource]);

  useEffect(() => {
    loadWeeks();
  }, [loadWeeks]);

  useEffect(() => {
    if (!archiveMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (archiveMenuRef.current && !archiveMenuRef.current.contains(event.target)) {
        setArchiveMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [archiveMenuOpen]);

  useEffect(() => {
    if (!teacherLoadMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (teacherLoadMenuRef.current && !teacherLoadMenuRef.current.contains(event.target)) {
        setTeacherLoadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [teacherLoadMenuOpen]);

  useEffect(() => {
    loadTeacherLoadFiles();
  }, [loadTeacherLoadFiles]);

  useEffect(() => {
    loadArchiveUploadFiles();
  }, [loadArchiveUploadFiles]);

  useEffect(() => {
    if (SOURCE_CODES.includes(selectedSource)) {
      setArchiveSource(selectedSource);
    }
  }, [selectedSource]);

  useEffect(() => {
    if (!archiveMenuOpen) {
      setArchiveUploadsExpanded(false);
      setArchiveOverlayNotice(null);
    }
  }, [archiveMenuOpen]);

  useEffect(() => {
    setArchiveUploadsExpanded(false);
  }, [archiveSource]);

  useEffect(() => () => {
    archiveOverlayTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
  }, []);

  useEffect(() => {
    if (!archiveMenuOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [archiveMenuOpen]);


  useEffect(() => {
    if (!archiveUploading) {
      setArchiveUploadDots(".");
      return undefined;
    }
    const frames = [".", "..", "..."];
    const timer = window.setInterval(() => {
      setArchiveUploadDots((prev) => {
        const nextIndex = (frames.indexOf(prev) + 1) % frames.length;
        return frames[nextIndex];
      });
    }, 450);
    return () => window.clearInterval(timer);
  }, [archiveUploading]);

  const handleArchiveFilesChange = async (event) => {
    const selected = Array.from(event.target.files || []);
    await processArchiveCandidateFiles(selected);
    if (archiveInputRef.current) {
      archiveInputRef.current.value = "";
    }
  };

  const handleArchiveDragOver = (event) => {
    event.preventDefault();
    if (archiveUploading) return;
    setArchiveDragActive(true);
  };

  const handleArchiveDragLeave = (event) => {
    event.preventDefault();
    const nextTarget = event.relatedTarget;
    if (event.currentTarget.contains(nextTarget)) return;
    setArchiveDragActive(false);
  };

  const handleArchiveDrop = async (event) => {
    event.preventDefault();
    if (archiveUploading) return;
    setArchiveDragActive(false);
    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    if (!droppedFiles.length) return;
    await processArchiveCandidateFiles(droppedFiles);
    if (archiveInputRef.current) {
      archiveInputRef.current.value = "";
    }
  };

  const removeArchiveFile = (targetFile) => {
    setArchiveFiles((prev) =>
      prev.filter(
        (file) =>
          !(
            file.name === targetFile.name &&
            file.size === targetFile.size &&
            file.lastModified === targetFile.lastModified
          )
      )
    );
    setArchiveMsg("");
    setArchiveLogs([]);
  };

  const clearArchiveSelection = () => {
    setArchiveFiles([]);
    setArchiveMsg("");
    setArchiveLogs([]);
    setArchiveDragActive(false);
    if (archiveInputRef.current) {
      archiveInputRef.current.value = "";
    }
  };

  const handleArchiveUpload = async () => {
    if (!archiveFiles.length) {
      setArchiveMsg("Выберите .xls или .xlsx файлы");
      return;
    }

    const invalidFile = archiveFiles.find((file) => {
      const lower = file.name.toLowerCase();
      return !lower.endsWith(".xls") && !lower.endsWith(".xlsx");
    });
    if (invalidFile) {
      setArchiveMsg(`Неподдерживаемый файл: ${invalidFile.name}`);
      return;
    }

    setArchiveUploading(true);
    setArchiveMsg("");
    setArchiveLogs([]);
    try {
      const body = new FormData();
      body.append("source", archiveSource);
      archiveFiles.forEach((file) => body.append("files", file));

      const res = await fetch(`${API}/schedule/archive-upload`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
        },
        body,
      });

      let payload = null;
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        payload = await res.json().catch(() => null);
      } else {
        const text = await res.text().catch(() => "");
        payload = text ? { detail: text } : null;
      }

      if (!res.ok) {
        const detail = payload?.detail;
        const message =
          typeof detail === "string"
            ? detail
            : detail?.message || payload?.message || `Ошибка ${res.status}`;
        const logs = Array.isArray(detail?.logs) ? detail.logs : [];
        const error = new Error(message);
        error.logs = logs;
        throw error;
      }

      const uploadedCount = payload?.count ?? archiveFiles.length;
      const jobId = payload?.job_id || "";
      if (jobId) {
        localStorage.setItem(ARCHIVE_IMPORT_JOB_KEY, jobId);
        localStorage.setItem(
          ARCHIVE_IMPORT_JOB_SNAPSHOT_KEY,
          JSON.stringify({
            job_id: jobId,
            source: payload?.source || archiveSource,
            status: payload?.status || "queued",
            progress_current: 0,
            progress_total: Math.max((payload?.count ?? archiveFiles.length) * 100, 1),
            current_step: "Задача поставлена в очередь",
            logs: [`Источник: ${payload?.source || archiveSource}`, `Загружено файлов: ${uploadedCount}`, "Ожидаем запуск фонового импорта"],
            files: payload?.files || [],
            archives: [],
            error_message: "",
          })
        );
        window.dispatchEvent(new CustomEvent("archive-import-job-changed", { detail: { jobId } }));
      }
      setArchiveMsg(`Импорт архива ${archiveSource} запущен в фоне`);
      setArchiveLogs([]);
      loadArchiveUploadFiles();
      clearArchiveSelection();
      setArchiveMenuOpen(false);
    } catch (e) {
      const message = e?.message || "Не удалось загрузить архивные Excel";
      const logs = Array.isArray(e?.logs) ? e.logs : [];
      const duplicateError = isArchiveDuplicateError(message, logs);
      if (duplicateError) {
        setArchiveMsg("");
        setArchiveLogs([]);
        showArchiveOverlayNotice(message, "warning");
      } else {
        setArchiveMsg(message);
        setArchiveLogs(logs);
        showToast(message, "error");
      }
    } finally {
      setArchiveUploading(false);
    }
  };

  const handleTeacherLoadFilesChange = (event) => {
    const selected = Array.from(event.target.files || []);
    setTeacherLoadMsg("");
    setTeacherLoadFiles(selected);
  };

  const clearTeacherLoadSelection = () => {
    setTeacherLoadFiles([]);
    setTeacherLoadMsg("");
    if (teacherLoadInputRef.current) {
      teacherLoadInputRef.current.value = "";
    }
  };

  const handleTeacherLoadUpload = async () => {
    if (!teacherLoadFiles.length) {
      setTeacherLoadMsg("Выберите .xls или .xlsx файлы");
      return;
    }

    const invalidFile = teacherLoadFiles.find((file) => {
      const lower = file.name.toLowerCase();
      return !lower.endsWith(".xls") && !lower.endsWith(".xlsx");
    });
    if (invalidFile) {
      setTeacherLoadMsg(`Неподдерживаемый файл: ${invalidFile.name}`);
      return;
    }

    setTeacherLoadUploading(true);
    setTeacherLoadMsg("");
    try {
      const body = new FormData();
      body.append("collection_name", teacherLoadCollectionName.trim());
      teacherLoadFiles.forEach((file) => body.append("files", file));

      const res = await fetch(`${API}/teacher-load/upload`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
        },
        body,
      });

      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(payload?.detail || payload?.message || `Ошибка ${res.status}`);
      }

      const uploadedCount = payload?.count ?? teacherLoadFiles.length;
      setTeacherLoadMsg(`Загружено файлов: ${uploadedCount}`);
      showToast(`Файлы нагрузки загружены: ${uploadedCount}`, "info");
      clearTeacherLoadSelection();
      setTeacherLoadCollectionName("");
      setTeacherLoadMenuOpen(false);
      loadTeacherLoadFiles();
    } catch (e) {
      const message = e?.message || "Не удалось загрузить файлы нагрузки";
      setTeacherLoadMsg(message);
      showToast(message, "error");
    } finally {
      setTeacherLoadUploading(false);
    }
  };

  const archiveRecentUploads = useMemo(
    () =>
      archiveUploadItems
        .filter(
          (item) =>
            (!archiveSource || item?.source === archiveSource) &&
            normalizeArchiveUploadStatus(item?.status) === "success"
        )
        .slice(0, 7),
    [archiveUploadItems, archiveSource]
  );

  const archivePrimaryUpload = archiveRecentUploads[0] || null;
  const archiveSecondaryUploads = archiveRecentUploads.slice(1);
  const archiveDetailUploads = archiveSecondaryUploads;

  /* --------- вычисляем недели периода --------- */
  const computedPeriodWeeks = useMemo(() => {
    if (!dateFrom || !dateTo) return [];

    const from = parseIsoDateLocal(dateFrom, false);
    const to = parseIsoDateLocal(dateTo, true);

    if (!from || !to) return [];
    if (from > to) return [];

    return weeks.filter(w => {
      const ws = parseWeekStart(w);
      const we = parseWeekEnd(w);
      if (!ws || !we) return false;
      // пересечение интервалов
      return !(we < from || ws > to);
    });
  }, [weeks, dateFrom, dateTo]);

  const availablePeriodDates = useMemo(() => {
    const dates = [];
    const seen = new Set();
    weeks.forEach((w) => {
      const ws = parseWeekStart(w);
      const we = parseWeekEnd(w);
      if (!ws || !we) return;
      const cursor = new Date(ws.getFullYear(), ws.getMonth(), ws.getDate());
      const end = new Date(we.getFullYear(), we.getMonth(), we.getDate());
      while (cursor <= end) {
        const iso = formatDateInputValue(cursor);
        if (!seen.has(iso)) {
          seen.add(iso);
          dates.push(iso);
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return dates.sort();
  }, [weeks]);
  const availablePeriodDateSet = useMemo(() => new Set(availablePeriodDates), [availablePeriodDates]);
  const calendarMonths = useMemo(() => (
    Array.from(new Set(availablePeriodDates.map((value) => toMonthKey(value))))
      .map((key) => {
        const date = monthDateFromKey(key);
        return date
          ? {
              key,
              date,
              label: date.toLocaleDateString("ru-RU", { month: "long", year: "numeric" }),
            }
          : null;
      })
      .filter(Boolean)
  ), [availablePeriodDates]);
  const activeCalendarMonthIndex = useMemo(
    () => Math.max(0, calendarMonths.findIndex((item) => item.key === calendarMonthKey)),
    [calendarMonthKey, calendarMonths]
  );
  const activeCalendarMonth = calendarMonths[activeCalendarMonthIndex] || null;
  const calendarCells = useMemo(
    () => buildCalendarMonthCells(activeCalendarMonth?.date || null),
    [activeCalendarMonth]
  );
  const todayIso = useMemo(() => formatDateInputValue(new Date()), []);
  const selectedRangeStart = useMemo(() => {
    if (!dateFrom && !dateTo) return "";
    if (!dateFrom) return dateTo;
    if (!dateTo) return dateFrom;
    return dateFrom <= dateTo ? dateFrom : dateTo;
  }, [dateFrom, dateTo]);
  const selectedRangeEnd = useMemo(() => {
    if (!dateFrom && !dateTo) return "";
    if (!dateFrom) return dateTo;
    if (!dateTo) return dateFrom;
    return dateFrom >= dateTo ? dateFrom : dateTo;
  }, [dateFrom, dateTo]);
  useEffect(() => {
    if (dateFrom && !availablePeriodDateSet.has(dateFrom)) {
      setDateFrom("");
    }
    if (dateTo && !availablePeriodDateSet.has(dateTo)) {
      setDateTo("");
    }
  }, [availablePeriodDateSet, dateFrom, dateTo]);

  useEffect(() => {
    if (!calendarMonths.length) {
      setCalendarMonthKey("");
      return;
    }
    setCalendarMonthKey((prev) => {
      if (prev && calendarMonths.some((item) => item.key === prev)) return prev;
      const now = new Date();
      const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const current = calendarMonths.find((item) => item.key === currentKey);
      return current ? current.key : calendarMonths[0].key;
    });
  }, [calendarMonths]);

  const showToast = (text, type = "error") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleCalendarDatePick = useCallback((iso) => {
    if (!iso || !availablePeriodDateSet.has(iso)) return;
    if (!selectedRangeStart || (selectedRangeStart && selectedRangeEnd && selectedRangeStart !== selectedRangeEnd)) {
      setDateFrom(iso);
      setDateTo(iso);
      return;
    }
    if (selectedRangeStart === selectedRangeEnd && selectedRangeStart === iso) {
      setDateFrom("");
      setDateTo("");
      return;
    }
    const start = selectedRangeStart <= iso ? selectedRangeStart : iso;
    const end = selectedRangeStart <= iso ? iso : selectedRangeStart;
    setDateFrom(start);
    setDateTo(end);
  }, [availablePeriodDateSet, selectedRangeEnd, selectedRangeStart]);

  const showPreviousCalendarMonth = useCallback(() => {
    if (activeCalendarMonthIndex <= 0) return;
    setCalendarDirection(-1);
    setCalendarMonthKey(calendarMonths[activeCalendarMonthIndex - 1].key);
  }, [activeCalendarMonthIndex, calendarMonths]);

  const showNextCalendarMonth = useCallback(() => {
    if (activeCalendarMonthIndex < 0 || activeCalendarMonthIndex >= calendarMonths.length - 1) return;
    setCalendarDirection(1);
    setCalendarMonthKey(calendarMonths[activeCalendarMonthIndex + 1].key);
  }, [activeCalendarMonthIndex, calendarMonths]);

  const activeWeeks = useMemo(() => computedPeriodWeeks, [computedPeriodWeeks]);


  /* --------- группы (зависят от недель + выбранных преподавателей) --------- */
  useEffect(() => {
    if (!activeWeeks.length) {
      setGroups([]);
      setGroupFilter([]);
      return;
    }

    const params = new URLSearchParams();
    activeWeeks.forEach(w => params.append("weeks", w));
    params.set("source", selectedSource);
    appendTeacherParams(params);

    fetch(`${API}/export/groups?${params.toString()}`)
      .then(res => res.json())
      .then(list => {
        const arr = Array.isArray(list) ? list : [];
        setGroups(arr);
        setGroupFilter(prev => prev.filter(g => arr.includes(g)));
      })
      .catch(() => {
        setGroups([]);
        setGroupFilter([]);
      });
  }, [activeWeeks, appendTeacherParams, selectedSource]);
  useEffect(() => {
    if (!activeWeeks.length) {
      setLessonTypes([]);
      setSelectedLessonTypes([]);
      return;
    }

    const params = new URLSearchParams();
    activeWeeks.forEach(w => params.append("weeks", w));
    params.set("source", selectedSource);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    groupFilter.forEach(g => params.append("group", g));
    disciplineFilter.forEach(d => params.append("discipline", d));
    appendTeacherParams(params);

    fetch(`${API}/export/lesson-types?${params.toString()}`)
      .then(r => r.json())
      .then(list => {
        const types = Array.isArray(list) ? list : [];
        setLessonTypes(types);
        setSelectedLessonTypes(prev => {
          if (!prev.length) return types;
          const kept = prev.filter(x => types.includes(x));
          return kept.length ? kept : types;
        });
      })
      .catch(() => {
        setLessonTypes([]);
        setSelectedLessonTypes([]);
      });
  }, [activeWeeks, appendTeacherParams, groupFilter, disciplineFilter, selectedSource, dateFrom, dateTo]);
  
  /* --------- дисциплины (недели + преподаватели + groupFilter) --------- */
  useEffect(() => {
    if (!activeWeeks.length) {
      setDisciplines([]);
      setDisciplineFilter([]);

      return;
    }

    const params = new URLSearchParams();
    activeWeeks.forEach(w => params.append("weeks", w));
    params.set("source", selectedSource);

    groupFilter.forEach(g => params.append("group", g));
    appendTeacherParams(params);

    fetch(`${API}/export/disciplines?${params.toString()}`)
      .then(res => res.json())
      .then(list => {
        const arr = Array.isArray(list) ? list : [];
        setDisciplines(arr);
        setDisciplineFilter(prev =>
          prev.filter(d => arr.includes(d))
        );
        
      })
      .catch(() => {
        setDisciplines([]);
        setDisciplineFilter([]);
      });
  }, [activeWeeks, appendTeacherParams, groupFilter, selectedSource]);

  /* --------- подсказки преподавателей (недели + группа + дисциплина) --------- */
  useEffect(() => {
    if (!activeWeeks.length) {
      setTeachers([]);
      return;
    }

    const params = new URLSearchParams();
    activeWeeks.forEach(w => params.append("weeks", w));
    params.set("source", selectedSource);

    groupFilter.forEach(g => params.append("group", g));
    disciplineFilter.forEach(d => {
      params.append("discipline", d);
    });
    

    fetch(`${API}/export/teachers?${params.toString()}`)
      .then(res => res.json())
      .then(data => setTeachers(Array.isArray(data) ? data : []))
      .catch(() => setTeachers([]));
  }, [activeWeeks, groupFilter, disciplineFilter, selectedSource]);

  /* --------- helpers --------- */
  const filteredTeachers = useMemo(() => {
    const q = normalize(teacherQuery);
    if (!q || q.length < 2) return [];
    return teachers
      .filter((t) => normalize(t).includes(q))
      .filter((t) => normalize(t) !== normalize(selectedTeacher))
      .slice(0, 12);
  }, [teacherQuery, teachers, selectedTeacher]);


  const toggleGroup = (g) => {
    setGroupFilter(prev =>
      prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]
    );
  };

  const selectAllGroups = () => setGroupFilter(groups);
  const clearGroups = () => setGroupFilter([]);

  const clearPeriodSelection = () => {
    setDateFrom("");
    setDateTo("");
  };

  /* --------- Excel --------- */
  const downloadExcel = () => {
  const exportTeacher = selectedTeacher.trim();

  // 1) период
  if (!activeWeeks.length) {
    showToast("Выберите период для выгрузки");
    return;
  }

  // 2) преподаватель
  if (!exportTeacher) {
    showToast("Выберите преподавателя");
    return;
  }

  // 3) слишком строгие фильтры (мягко)
  if (!groups.length) {
    showToast("По выбранным фильтрам нет подходящих занятий", "info");
    return;
  }
  if (lessonTypes.length && selectedLessonTypes.length === 0) {
    showToast("Выберите хотя бы один тип занятия");
    return;
  }
  setLoading(true);

  const params = new URLSearchParams();
  activeWeeks.forEach(w => params.append("weeks", w));
  params.set("source", selectedSource);
  params.set("teacher", exportTeacher);
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  groupFilter.forEach(g => params.append("group", g));
  disciplineFilter.forEach(d => {
    params.append("discipline", d);
  });
  if (selectedLessonTypes.length && selectedLessonTypes.length !== lessonTypes.length) {
    selectedLessonTypes.forEach(t => params.append("lesson_type", t));
  }
  

  window.location.href = `${API}/export/teacher-period?${params.toString()}`;
  setTimeout(() => setLoading(false), 1200);
};

const canDownload = useMemo(() => {
  if (!activeWeeks.length) return false;
  if (!selectedTeacher.trim()) return false;
  return true;
}, [activeWeeks, selectedTeacher]);

const canShowFilters = Boolean(selectedRangeStart && selectedRangeEnd && selectedTeacher.trim());
const hasSelectedPeriod = Boolean(selectedRangeStart || selectedRangeEnd);

useEffect(() => {
  setFiltersOpen(canShowFilters);
}, [canShowFilters]);


  /* ================= UI ================= */
  return (
    <>
      <div className="teacher-export-shell flex justify-center -mt-8">
        <div className="max-w-lg w-full rounded-[28px] border border-black/[0.06] bg-[#f2f2f7]/95 px-4 pb-5 pt-5 shadow-[0_8px_40px_-8px_rgba(0,0,0,0.14)] backdrop-blur-2xl">

          {/* header — iOS style */}
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-[22px] font-bold tracking-[-0.4px] text-[#1c1c1e] pl-[8px]">
              Нагрузка
            </h2>

            <div className="flex items-center gap-2">
              {/* Архив расписания - только для admin и support */}
              {canManageParser && (
                <div className="relative" ref={archiveMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setArchiveMenuOpen(true);
                      setArchiveMsg("");
                    }}
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition-all duration-200 ${
                    archiveMenuOpen
                      ? "bg-[#007aff] text-white shadow-[0_4px_12px_rgba(0,122,255,0.4)]"
                      : "bg-black/[0.07] text-[#3c3c43]/60 hover:bg-black/10"
                  }`}
                  aria-label="Архив расписаний"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110">
                    <path fill="currentColor" d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5V7a2 2 0 0 1-1.2 1.84V18a2 2 0 0 1-2 2H7.2a2 2 0 0 1-2-2V8.84A2 2 0 0 1 4 7V5.5Zm2.8 3.5V18h10V9H6.8ZM6.5 4.6a.9.9 0 0 0-.9.9V7c0 .22.18.4.4.4h12c.22 0 .4-.18.4-.4V5.5a.9.9 0 0 0-.9-.9h-11Zm3.5 7.15c0-.44.36-.8.8-.8h2.4c.44 0 .8.36.8.8s-.36.8-.8.8h-2.4a.8.8 0 0 1-.8-.8Z" />
                  </svg>
                </button>

                <div
                  className={`absolute right-0 top-[calc(100%+0.5rem)] z-30 w-[min(360px,calc(100vw-2rem))] rounded-[22px] border border-black/[0.07] bg-white/96 p-4 shadow-[0_8px_32px_rgba(0,0,0,0.16)] backdrop-blur-2xl transition-all duration-200 ${
                    archiveMenuOpen
                      ? "pointer-events-auto translate-y-0 opacity-100"
                      : "pointer-events-none -translate-y-1 opacity-0"
                  }`}
                >
                  <div className="relative mb-3 flex items-center justify-between">
                    <div className="text-[15px] font-semibold text-[#1c1c1e]">Архив расписания</div>
                    <button
                      type="button"
                      onClick={() => setArchiveMenuOpen(false)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/[0.07] text-[#3c3c43]/70 transition hover:bg-black/10"
                      aria-label="Закрыть"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5">
                        <path fill="currentColor" d="m6.7 5.3 5.3 5.3 5.3-5.3a1 1 0 1 1 1.4 1.4L13.4 12l5.3 5.3a1 1 0 0 1-1.4 1.4L12 13.4l-5.3 5.3a1 1 0 0 1-1.4-1.4l5.3-5.3-5.3-5.3a1 1 0 0 1 1.4-1.4Z" />
                      </svg>
                    </button>
                  </div>

                  <div className="mt-0">
                    <div className="flex w-full items-center gap-1 rounded-[10px] bg-[#e9e9eb] p-[3px]">
                        {ARCHIVE_SOURCE_CODES.map((code) => {
                          const active = archiveSource === code;
                          return (
                            <button
                              key={code}
                              type="button"
                              onClick={() => setArchiveSource(code)}
                              disabled={archiveUploading}
                               className={`flex-1 rounded-[8px] py-1.5 text-center text-[13px] font-medium transition-all duration-200 ${
                                active
                                  ? "bg-white text-[#1c1c1e] shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
                                  : "text-[#3c3c43]/60"
                              } ${archiveUploading ? "cursor-not-allowed opacity-50" : ""}`}
                            >
                              {code}
                            </button>
                          );
                        })}
                    </div>

                    <div
                      onDragOver={handleArchiveDragOver}
                      onDragEnter={handleArchiveDragOver}
                      onDragLeave={handleArchiveDragLeave}
                      onDrop={handleArchiveDrop}
                      onClick={() => archiveInputRef.current?.click()}
                      className={`relative mt-3 cursor-pointer rounded-[16px] border-2 border-dashed px-4 py-6 transition-all duration-200 ${
                        archiveDragActive
                          ? "border-[#007aff] bg-[#007aff]/[0.05]"
                          : "border-[#c6c6c8] bg-[#f2f2f7]/60"
                      }`}
                    >
                      <input
                        ref={archiveInputRef}
                        type="file"
                        accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        multiple
                        onChange={handleArchiveFilesChange}
                        className="hidden"
                      />

                      {archiveOverlayNotice ? (
                        <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden rounded-[14px]">
                          <div className="absolute inset-0 bg-white/60 backdrop-blur-md" />
                          <div className="absolute inset-0 flex items-center justify-center px-4">
                            <div
                              className={`max-w-[220px] rounded-2xl px-4 py-3 text-center text-[13px] font-medium shadow-lg transition-all duration-300 ${
                                archiveOverlayNotice.visible ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0"
                              } ${
                                archiveOverlayNotice.tone === "warning"
                                  ? "bg-white text-[#ff3b30] shadow-[0_4px_20px_rgba(255,59,48,0.15)]"
                                  : archiveOverlayNotice.tone === "success"
                                    ? "bg-white text-[#34c759]"
                                    : "bg-white text-[#ff3b30]"
                              }`}
                            >
                              {archiveOverlayNotice.text}
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <div className="flex flex-col items-center justify-center text-center">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-200 ${archiveDragActive ? "bg-[#007aff]/10 text-[#007aff]" : "bg-[#e5e5ea] text-[#8e8e93]"}`}>
                          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                            <path fill="currentColor" d="M12 3.75a.75.75 0 0 1 .75.75v8.69l2.47-2.47a.75.75 0 1 1 1.06 1.06l-3.75 3.75a.75.75 0 0 1-1.06 0L7.72 11.78a.75.75 0 1 1 1.06-1.06l2.47 2.47V4.5a.75.75 0 0 1 .75-.75ZM4.5 19.5a.75.75 0 0 1 .75-.75h13.5a.75.75 0 0 1 0 1.5H5.25a.75.75 0 0 1-.75-.75Z" />
                          </svg>
                        </div>
                        <div className="mt-1.5 text-[13px] font-semibold text-[#1c1c1e]">Выбрать файлы</div>
                        <div className="mt-0.5 text-[11px] text-[#8e8e93]">Excel .xls / .xlsx</div>
                      </div>

                      {archiveFiles.length > 0 ? (
                        <div className="mt-3 space-y-1.5">
                            {archiveFiles.map((file) => (
                              <div
                                key={`${file.name}-${file.size}-${file.lastModified}`}
                                className="flex items-center gap-2.5 rounded-[12px] bg-white px-3 py-2 shadow-[0_1px_4px_rgba(0,0,0,0.08)]"
                              >
                                <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#e8f5ee]">
                                  <ExcelFileIcon className="h-7 w-7" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-[13px] font-medium text-[#1c1c1e]">{file.name}</div>
                                  <div className="text-[11px] text-[#8e8e93]">{formatFileSize(file.size)}</div>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); removeArchiveFile(file); }}
                                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#e5e5ea] text-[#8e8e93] transition hover:bg-[#d1d1d6]"
                                  aria-label={`Убрать файл ${file.name}`}
                                >
                                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5">
                                    <path fill="currentColor" d="m6.7 5.3 5.3 5.3 5.3-5.3a1 1 0 1 1 1.4 1.4L13.4 12l5.3 5.3a1 1 0 0 1-1.4 1.4L12 13.4l-5.3 5.3a1 1 0 0 1-1.4-1.4l5.3-5.3-5.3-5.3a1 1 0 0 1 1.4-1.4Z" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {archiveMsg ? (
                    <div
                      className={`mt-2.5 rounded-[12px] px-3 py-2 text-[13px] ${
                        isArchiveDuplicateError(archiveMsg, archiveLogs)
                          ? "bg-[#fff9e6] text-[#b7791f]"
                          : archiveMsg.startsWith("Загружено") || archiveMsg.includes("запущен")
                            ? "bg-[#f0fdf4] text-[#15803d]"
                            : "bg-[#fff1f2] text-[#be123c]"
                      }`}
                    >
                      {archiveMsg}
                    </div>
                  ) : null}

                  {archiveLogs.length > 0 ? (
                    <div className="mt-3 rounded-2xl border border-rose-100 bg-rose-50/80 px-3 py-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-rose-700">
                        Логи парсинга
                      </div>
                      <div className="mt-2 max-h-40 space-y-1 overflow-auto pr-1 font-mono text-[11px] leading-5 text-rose-800">
                        {archiveLogs.map((line, index) => (
                          <div key={`${index}-${line}`}>{line}</div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {archivePrimaryUpload ? (
                    <div className="mt-3">
                      <div className="mb-1.5 flex items-center gap-2">
                        <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#8e8e93]">
                          Недавние
                        </div>
                        {archiveSecondaryUploads.length > 0 && (
                          <span className="rounded-full bg-[#e5e5ea] px-1.5 py-0.5 text-[10px] text-[#8e8e93]">
                            {archiveRecentUploads.length}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        {[archivePrimaryUpload].map((item) => (
                          <div
                            key={item.id || `${item.saved_name}-${item.uploaded_at}`}
                            className="flex items-center gap-2.5 rounded-[12px] bg-white px-3 py-2 shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
                          >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#e8f5ee]">
                              <ExcelFileIcon className="h-7 w-7" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[13px] font-medium text-[#1c1c1e]">{item.original_name}</div>
                              <div className="text-[11px] text-[#8e8e93]">
                                {formatFileSize(item.size)}{item.uploaded_at ? ` · ${formatArchiveUploadedAt(item.uploaded_at)}` : ""}
                              </div>
                            </div>
                            {archiveSecondaryUploads.length > 0 ? (
                              <button
                                type="button"
                                onClick={() => setArchiveUploadsExpanded((prev) => !prev)}
                                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#e5e5ea] text-[#8e8e93] transition hover:bg-[#d1d1d6]"
                                aria-label={archiveUploadsExpanded ? "Скрыть" : "Показать ещё"}
                              >
                                <svg viewBox="0 0 24 24" aria-hidden="true" className={`h-3.5 w-3.5 transition-transform ${archiveUploadsExpanded ? "rotate-90" : ""}`}>
                                  <path fill="currentColor" d="M9.47 6.97a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 1 1-1.06-1.06L13.44 12 9.47 8.03a.75.75 0 0 1 0-1.06Z" />
                                </svg>
                              </button>
                            ) : null}
                          </div>
                        ))}
                        {archiveUploadsExpanded && archiveSecondaryUploads.length > 0 ? (
                          <div className="max-h-28 space-y-1 overflow-auto">
                            {archiveSecondaryUploads.map((item) => (
                              <div
                                key={item.id || `${item.saved_name}-${item.uploaded_at}`}
                                className="flex items-center gap-2.5 rounded-[12px] bg-white px-3 py-2 shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
                              >
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#e8f5ee]">
                                  <ExcelFileIcon className="h-7 w-7" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-[13px] font-medium text-[#1c1c1e]">{item.original_name}</div>
                                  <div className="text-[11px] text-[#8e8e93]">{formatFileSize(item.size)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-3 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={clearArchiveSelection}
                      className="text-[13px] font-medium text-[#8e8e93] transition hover:text-[#3c3c43]"
                    >
                      Сбросить
                    </button>
                    <button
                      type="button"
                      onClick={handleArchiveUpload}
                      disabled={archiveUploading}
                      className={`inline-flex items-center justify-center rounded-[12px] px-5 py-2 text-[14px] font-semibold transition-all ${
                        archiveUploading
                          ? "cursor-wait bg-[#e5e5ea] text-[#8e8e93]"
                          : "bg-[#007aff] text-white shadow-[0_4px_14px_rgba(0,122,255,0.35)] active:scale-[0.97]"
                      }`}
                    >
                      {archiveUploading ? `Импорт${archiveUploadDots}` : "Загрузить"}
                    </button>
                  </div>
                </div>
              </div>
              )}

              {canManageParser && (
                <div className="relative" ref={teacherLoadMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setTeacherLoadMenuOpen((v) => !v);
                      setTeacherLoadMsg("");
                    }}
                    className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition-all duration-200 ${
                      teacherLoadMenuOpen
                        ? "bg-[#34c759] text-white shadow-[0_4px_12px_rgba(52,199,89,0.4)]"
                        : "bg-black/[0.07] text-[#3c3c43]/60 hover:bg-black/10"
                    }`}
                    aria-label="Файлы нагрузки"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4.5 w-4.5">
                      <path fill="currentColor" d="M7 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.41a2 2 0 0 0-.59-1.41l-3.41-3.41A2 2 0 0 0 13.59 3H7Zm6 1.5V8h3.5L13 4.5ZM8.5 11a.75.75 0 0 1 .75-.75h5.5a.75.75 0 0 1 0 1.5h-5.5A.75.75 0 0 1 8.5 11Zm0 3.5a.75.75 0 0 1 .75-.75h5.5a.75.75 0 0 1 0 1.5h-5.5a.75.75 0 0 1-.75-.75Z" />
                    </svg>
                  </button>

                  <div
                    className={`absolute right-0 top-[calc(100%+0.5rem)] z-30 w-[min(320px,calc(100vw-2rem))] rounded-[22px] border border-black/[0.07] bg-white/96 p-4 shadow-[0_8px_32px_rgba(0,0,0,0.16)] backdrop-blur-2xl transition-all duration-200 ${
                      teacherLoadMenuOpen
                        ? "pointer-events-auto translate-y-0 opacity-100"
                        : "pointer-events-none -translate-y-2 opacity-0"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="text-[15px] font-semibold text-[#1c1c1e]">Файлы нагрузки</div>
                      <button
                        type="button"
                        onClick={() => setTeacherLoadMenuOpen(false)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/[0.07] text-[#3c3c43]/70 transition hover:bg-black/10"
                        aria-label="Закрыть"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5">
                          <path fill="currentColor" d="m6.7 5.3 5.3 5.3 5.3-5.3a1 1 0 1 1 1.4 1.4L13.4 12l5.3 5.3a1 1 0 0 1-1.4 1.4L12 13.4l-5.3 5.3a1 1 0 0 1-1.4-1.4l5.3-5.3-5.3-5.3a1 1 0 0 1 1.4-1.4Z" />
                        </svg>
                      </button>
                    </div>

                    <div className="mb-3">
                      <input
                        type="text"
                        value={teacherLoadCollectionName}
                        onChange={(e) => setTeacherLoadCollectionName(e.target.value)}
                        placeholder="Название набора"
                        className="w-full rounded-[12px] border border-black/[0.1] bg-[#f2f2f7] px-3 py-2.5 text-[14px] text-[#1c1c1e] outline-none placeholder:text-[#8e8e93] focus:border-[#34c759]/60 focus:bg-white transition"
                      />
                    </div>

                    <div className="rounded-[14px] border-2 border-dashed border-[#c6c6c8] bg-[#f2f2f7]/60 p-3">
                      <input
                        ref={teacherLoadInputRef}
                        type="file"
                        accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        multiple
                        onChange={handleTeacherLoadFilesChange}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => teacherLoadInputRef.current?.click()}
                        className="flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#1c1c1e] px-4 py-2.5 text-[13px] font-medium text-white transition active:scale-[0.97]"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
                          <path fill="currentColor" d="M12 3.75a.75.75 0 0 1 .75.75v8.69l2.47-2.47a.75.75 0 1 1 1.06 1.06l-3.75 3.75a.75.75 0 0 1-1.06 0L8.72 11.78a.75.75 0 1 1 1.06-1.06l2.47 2.47V4.5a.75.75 0 0 1 .75-.75ZM4.5 19.5a.75.75 0 0 1 .75-.75h13.5a.75.75 0 0 1 0 1.5H5.25a.75.75 0 0 1-.75-.75Z" />
                        </svg>
                        Выбрать файлы
                      </button>

                      {teacherLoadFiles.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          {teacherLoadFiles.map((file) => (
                            <div key={`${file.name}-${file.size}`} className="rounded-[10px] bg-white px-3 py-2 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
                              <div className="truncate text-[13px] font-medium text-[#1c1c1e]">{file.name}</div>
                              <div className="text-[11px] text-[#8e8e93]">{formatFileSize(file.size)}</div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    {teacherLoadMsg ? (
                      <div className={`mt-2 rounded-[10px] px-3 py-2 text-[13px] ${teacherLoadMsg.startsWith("Загружено") ? "bg-[#f0fdf4] text-[#15803d]" : "bg-[#fff1f2] text-[#be123c]"}`}>
                        {teacherLoadMsg}
                      </div>
                    ) : null}

                    {teacherLoadItems.length > 0 ? (
                      <div className="mt-3 rounded-[12px] bg-[#f2f2f7] px-3 py-2.5">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#8e8e93] mb-1.5">
                          История
                        </div>
                        <div className="max-h-32 space-y-1 overflow-auto">
                          {teacherLoadItems.slice(0, 5).map((item) => (
                            <div key={item.id || `${item.saved_name}-${item.uploaded_at}`} className="rounded-[8px] bg-white px-3 py-1.5">
                              <div className="truncate text-[12px] font-medium text-[#1c1c1e]">{item.collection_name || "Без названия"}</div>
                              <div className="truncate text-[11px] text-[#8e8e93]">{item.original_name}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={clearTeacherLoadSelection}
                        className="text-[13px] font-medium text-[#8e8e93] transition hover:text-[#3c3c43]"
                      >
                        Сбросить
                      </button>
                      <button
                        type="button"
                        onClick={handleTeacherLoadUpload}
                        disabled={teacherLoadUploading}
                        className={`inline-flex items-center justify-center rounded-[12px] px-5 py-2 text-[14px] font-semibold transition-all ${
                          teacherLoadUploading
                            ? "cursor-wait bg-[#e5e5ea] text-[#8e8e93]"
                            : "bg-[#34c759] text-white shadow-[0_4px_14px_rgba(52,199,89,0.35)] active:scale-[0.97]"
                        }`}
                      >
                        {teacherLoadUploading ? "Загружаем..." : "Загрузить"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Source switcher — iOS segmented control */}
          <div className="mb-4 mt-4">
            <div className="flex w-full items-center gap-1 rounded-[11px] bg-[#e9e9eb] p-[3px]">
              {SOURCE_CODES.map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setSelectedSource(src)}
                  className={`flex-1 rounded-[9px] py-1.5 text-[13px] font-medium transition-all duration-200 ${
                    selectedSource === src
                      ? "bg-white text-[#1c1c1e] shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
                      : "text-[#3c3c43]/60"
                  }`}
                >
                  {src}
                </button>
              ))}
            </div>
          </div>

          <div className="relative">

          {activeCalendarMonth ? (
            <div className="rounded-[20px] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)] overflow-hidden">
              {/* Month nav */}
              <div className="flex items-center justify-between px-4 pt-3 pb-2">
                <button
                  type="button"
                  onClick={showPreviousCalendarMonth}
                  disabled={activeCalendarMonthIndex === 0}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition ${
                    activeCalendarMonthIndex === 0
                      ? "text-[#c7c7cc] cursor-not-allowed"
                      : "bg-[#f2f2f7] text-[#007aff] active:scale-95"
                  }`}
                  aria-label="Предыдущий месяц"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
                    <path fill="currentColor" d="M14.53 6.97a.75.75 0 0 1 0 1.06L10.56 12l3.97 3.97a.75.75 0 1 1-1.06 1.06l-4.5-4.5a.75.75 0 0 1 0-1.06l4.5-4.5a.75.75 0 0 1 1.06 0Z" />
                  </svg>
                </button>
                <div className="text-[14px] font-semibold text-[#1c1c1e] capitalize">
                  {activeCalendarMonth.label}
                </div>
                <button
                  type="button"
                  onClick={showNextCalendarMonth}
                  disabled={activeCalendarMonthIndex >= calendarMonths.length - 1}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition ${
                    activeCalendarMonthIndex >= calendarMonths.length - 1
                      ? "text-[#c7c7cc] cursor-not-allowed"
                      : "bg-[#f2f2f7] text-[#007aff] active:scale-95"
                  }`}
                  aria-label="Следующий месяц"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
                    <path fill="currentColor" d="M9.47 17.03a.75.75 0 0 1 0-1.06L13.44 12 9.47 8.03a.75.75 0 1 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06 0Z" />
                  </svg>
                </button>
              </div>

             <div className="mt-1 flex min-h-[40px] items-center justify-between gap-2 px-8 pb-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  {hasSelectedPeriod ? (
                    <>
                      {selectedRangeStart ? (
                        <span className="inline-flex items-center rounded-full bg-[#e8f0fe] px-2.5 py-1 text-[11px] font-semibold text-[#007aff]/70">
                          С {formatPrettyDate(selectedRangeStart)}
                        </span>
                      ) : null}
                      {selectedRangeEnd && selectedRangeEnd !== selectedRangeStart ? (
                        <span className="inline-flex items-center rounded-full bg-[#ffecec] px-2.5 py-1 text-[11px] font-semibold text-[#ff3b30]/70">
                          По {formatPrettyDate(selectedRangeEnd)}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-[13px] text-[#8e8e93]">Выберите период</span>
                  )}
                </div>
                
                {hasSelectedPeriod && (
                  <button onClick={clearPeriodSelection} className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#e5e5ea] text-[#8e8e93] mr-2" aria-label="Сбросить">
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5">
                      <path fill="currentColor" d="m6.7 5.3 5.3 5.3 5.3-5.3a1 1 0 1 1 1.4 1.4L13.4 12l5.3 5.3a1 1 0 0 1-1.4 1.4L12 13.4l-5.3 5.3a1 1 0 0 1-1.4-1.4l5.3-5.3-5.3-5.3a1 1 0 0 1 1.4-1.4Z" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Calendar grid */}
              <div className="px-2 pb-3">
                <div key={calendarMonthKey} className={`grid grid-cols-7 gap-0 text-center mb-1 ${calendarDirection >= 0 ? "calendar-month-slide-forward" : "calendar-month-slide-back"}`}>
                  {CALENDAR_WEEKDAYS.map((weekday) => (
                    <div key={weekday} className={`py-1 text-[11px] font-semibold ${weekday === "Сб" || weekday === "Вс" ? "text-[#ff3b30]" : "text-[#8e8e93]"}`}>
                      {weekday}
                    </div>
                  ))}
                  {calendarCells.map((cell, index) => {
                    if (!cell) return <div key={`empty-${index}`} className="mx-auto h-10 w-10" aria-hidden="true" />;
                    const isAvailable = availablePeriodDateSet.has(cell.iso);
                    const isStart = selectedRangeStart === cell.iso;
                    const isEnd = selectedRangeEnd === cell.iso;
                    const isSingleDay = isStart && isEnd;
                    const isToday = cell.iso === todayIso;
                    const isWeekendColumn = index % 7 >= 5;
                    const isInRange = isAvailable && selectedRangeStart && selectedRangeEnd && cell.iso >= selectedRangeStart && cell.iso <= selectedRangeEnd;
                    return (
                      <button key={cell.iso} type="button" onClick={() => handleCalendarDatePick(cell.iso)} disabled={!isAvailable}
                        className={`relative mx-auto flex h-10 w-10 items-center justify-center overflow-hidden rounded-full text-[14px] font-medium transition-all active:scale-90 [-webkit-tap-highlight-color:transparent] ${
                          !isAvailable ? "cursor-not-allowed text-[#c7c7cc]"
                          : isStart || isEnd || isSingleDay ? "calendar-day-pop text-white"
                          : isInRange ? "text-[#1c1c1e]"
                          : isToday ? "font-semibold text-[#007aff]"
                          : isWeekendColumn ? "text-[#ff3b30] hover:bg-[#f2f2f7]"
                          : "text-[#1c1c1e] hover:bg-[#f2f2f7]"
                        }`}
                      >
                        {isInRange && !isStart && !isEnd && <span className="absolute inset-0 bg-[#007aff]/10" />}
                        {(isStart && !isSingleDay) && <span className="absolute inset-0 rounded-full bg-[#007aff]/75" />}
                        {(isEnd && !isSingleDay) && <span className="absolute inset-0 rounded-full bg-[#ff3b30]/75" />}
                        {isSingleDay && <span className="absolute inset-0 rounded-full bg-[#007aff]/75" />}
                        {isToday && !isStart && !isEnd && !isSingleDay && <span className="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#007aff]" />}
                        <span className="relative z-10">{cell.dayNumber}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-[20px] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)] px-4 py-8 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#f2f2f7]">
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-6 w-6 text-[#8e8e93]">
                  <path fill="currentColor" d="M8 2a.75.75 0 0 1 .75.75V4h6.5V2.75a.75.75 0 0 1 1.5 0V4H18a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1.25V2.75A.75.75 0 0 1 8 2Zm-2 5.5v1.5h12V7.5H6Zm0 3V20h12V10.5H6Z" />
                </svg>
              </div>
              <div className="text-[14px] font-medium text-[#3c3c43]">Нет дат для источника</div>
              <div className="mt-1 text-[12px] text-[#8e8e93]">Загрузите архив расписания</div>
            </div>
          )}
          </div>

          {/* Filters panel */}
          <div className={`mt-3 overflow-hidden transition-all duration-300 ease-out ${filtersOpen ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"}`}>
            <div className="rounded-[20px] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)] divide-y divide-[#f2f2f7] overflow-hidden">
              <div className="px-4 py-3">
                <div className="flex justify-between items-center mb-2.5">
                  <div className="text-[13px] font-semibold text-[#1c1c1e]">
                    Группы{groupFilter.length > 0 && <span className="ml-1.5 rounded-full bg-[#007aff] px-1.5 py-0.5 text-[10px] text-white">{groupFilter.length}</span>}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={selectAllGroups} className="text-[13px] text-[#007aff] font-medium">Все</button>
                    <button onClick={clearGroups} className="text-[13px] text-[#8e8e93]">Сбросить</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-36 overflow-auto">
                  {groups.map(g => {
                    const active = groupFilter.includes(g);
                    return (
                      <button key={g} onClick={() => toggleGroup(g)} className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition-all duration-150 ${active ? "bg-[#007aff] text-white" : "bg-[#f2f2f7] text-[#1c1c1e]"}`}>{g}</button>
                    );
                  })}
                </div>
              </div>
              <div className="px-4 py-3">
                <div className="text-[13px] font-semibold text-[#1c1c1e] mb-2.5">Тип занятия</div>
                {lessonTypes.length === 0 ? (
                  <p className="text-[13px] text-[#8e8e93]">Нет данных</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {lessonTypes.map(type => (
                      <button key={type} onClick={() => setSelectedLessonTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type])}
                        className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition-all duration-150 ${getLessonTypeClass(type, selectedLessonTypes.includes(type))}`}
                      >{type}</button>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-4 py-3">
                <div className="text-[13px] font-semibold text-[#1c1c1e] mb-2.5">
                  Дисциплина{disciplineFilter.length > 0 && <span className="ml-1.5 rounded-full bg-[#5856d6] px-1.5 py-0.5 text-[10px] text-white">{disciplineFilter.length}</span>}
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-36 overflow-auto">
                  {disciplines.map(d => {
                    const active = disciplineFilter.includes(d);
                    const short = d.length > 28 ? d.slice(0, 28) + "…" : d;
                    return (
                      <button key={d} title={d} onClick={() => setDisciplineFilter(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
                        className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition-all duration-150 ${active ? "bg-[#5856d6] text-white" : "bg-[#f2f2f7] text-[#1c1c1e]"}`}
                      >{short}</button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Teacher search */}
          <div className="mt-3">
            <div className="relative">
              <div className={`flex min-h-[52px] w-full items-center gap-2 rounded-[16px] bg-white px-3 shadow-[0_2px_12px_rgba(0,0,0,0.08)] transition focus-within:shadow-[0_0_0_2px_rgba(0,122,255,0.3)]`}>
                {!selectedTeacher && (
                  <span className="shrink-0 text-[#8e8e93]">
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                      <path fill="currentColor" d="M10.5 4.75a5.75 5.75 0 1 0 3.61 10.22l3.96 3.96a.75.75 0 1 0 1.06-1.06l-3.96-3.96A5.75 5.75 0 0 0 10.5 4.75Zm0 1.5a4.25 4.25 0 1 1 0 8.5a4.25 4.25 0 0 1 0-8.5Z" />
                    </svg>
                  </span>
                )}
                {selectedTeacher && (
                  <button type="button" onClick={clearTeacherSelection}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#007aff]/10 px-2.5 py-1 text-[13px] font-medium text-[#007aff] transition hover:bg-[#007aff]/20"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5 shrink-0">
                      <path fill="currentColor" d="M12 2a5 5 0 1 1 0 10A5 5 0 0 1 12 2Zm0 12c5.33 0 8 2.67 8 4v2H4v-2c0-1.33 2.67-4 8-4Z" />
                    </svg>
                    <span className="max-w-[160px] truncate">{selectedTeacher}</span>
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3 w-3 text-[#007aff]/60"><path fill="currentColor" d="m6.7 5.3 5.3 5.3 5.3-5.3a1 1 0 1 1 1.4 1.4L13.4 12l5.3 5.3a1 1 0 0 1-1.4 1.4L12 13.4l-5.3 5.3a1 1 0 0 1-1.4-1.4l5.3-5.3-5.3-5.3a1 1 0 0 1 1.4-1.4Z" /></svg>
                  </button>
                )}
                <input type="text" value={teacherQuery}
                  onChange={e => { setTeacherQuery(e.target.value); setShowTeacherSuggestions(true); }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    const exact = teachers.find((item) => normalize(item) === normalize(teacherQuery));
                    if (!exact) return;
                    e.preventDefault();
                    addTeacherSelection(exact);
                  }}
                  onFocus={() => { if (teacherBlurTimer.current) { clearTimeout(teacherBlurTimer.current); teacherBlurTimer.current = null; } setShowTeacherSuggestions(true); }}
                  onBlur={() => { teacherBlurTimer.current = setTimeout(() => setShowTeacherSuggestions(false), 150); }}
                  placeholder={selectedTeacher ? "" : "Фамилия преподавателя"}
                  className="min-w-0 flex-1 bg-transparent py-3 text-[15px] text-[#1c1c1e] outline-none placeholder:text-[#c7c7cc]"
                />
              </div>
              {showTeacherSuggestions && filteredTeachers.length > 0 && (
                <div className="absolute z-20 mt-1.5 w-full max-h-56 overflow-auto rounded-[16px] bg-white shadow-[0_8px_32px_rgba(0,0,0,0.14)] backdrop-blur-sm">
                  {filteredTeachers.map((t, idx) => (
                    <button key={t} type="button"
                      onMouseDown={(e) => { e.preventDefault(); addTeacherSelection(t); }}
                      className={`flex w-full items-center gap-2.5 px-4 py-3 text-left text-[15px] text-[#1c1c1e] hover:bg-[#f2f2f7] ${idx > 0 ? "border-t border-[#f2f2f7]" : ""}`}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 shrink-0 text-[#8e8e93]">
                        <path fill="currentColor" d="M12 2a5 5 0 1 1 0 10A5 5 0 0 1 12 2Zm0 12c5.33 0 8 2.67 8 4v2H4v-2c0-1.33 2.67-4 8-4Z" />
                      </svg>
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Download button */}
          <button onClick={downloadExcel} disabled={!canDownload || loading}
            className={`mt-3 w-full rounded-[16px] py-4 text-[16px] font-semibold transition-all duration-200 ${
              !canDownload
                ? "bg-[#e5e5ea] text-[#8e8e93] cursor-not-allowed"
                : loading
                  ? "bg-[#007aff]/60 text-white cursor-wait"
                  : "bg-[#007aff] text-white shadow-[0_4px_16px_rgba(0,122,255,0.35)] active:scale-[0.98] active:shadow-none"
            }`}
          >
            {loading ? "Формирование…"
              : !activeWeeks.length ? "Выберите период"
              : !selectedTeacher.trim() ? "Выберите преподавателя"
              : (
                <span className="flex items-center justify-center gap-2">
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                    <path fill="currentColor" d="M12 3.75a.75.75 0 0 1 .75.75v8.69l2.47-2.47a.75.75 0 1 1 1.06 1.06l-3.75 3.75a.75.75 0 0 1-1.06 0L8.72 11.78a.75.75 0 1 1 1.06-1.06l2.47 2.47V4.5a.75.75 0 0 1 .75-.75ZM4.5 19.5a.75.75 0 0 1 .75-.75h13.5a.75.75 0 0 1 0 1.5H5.25a.75.75 0 0 1-.75-.75Z" />
                  </svg>
                  Excel
                </span>
              )
            }
          </button>

        </div>
      </div>
      {false && archiveMenuOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-2 py-2 sm:px-4 sm:py-3">
          <button
            type="button"
            aria-label="Закрыть окно загрузки архива"
            className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.72),rgba(226,232,240,0.84)_42%,rgba(148,163,184,0.22))] backdrop-blur-[10px]"
            onClick={() => setArchiveMenuOpen(false)}
          />
          <div className="relative z-10 flex max-h-[74vh] w-full max-w-[560px] flex-col overflow-hidden rounded-[22px] border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(248,250,252,0.94))] shadow-[0_22px_56px_rgba(15,23,42,0.12)]">
            <div className="flex items-center justify-between gap-3 px-3.5 pt-3.5 sm:px-5 sm:pt-5">
              <div className="text-base font-semibold tracking-tight text-slate-800 sm:text-[21px]">
                Загрузка архива расписания
              </div>
              <button
                type="button"
                onClick={() => setArchiveMenuOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Закрыть меню архива"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                  <path fill="currentColor" d="m6.7 5.3 5.3 5.3 5.3-5.3a1 1 0 1 1 1.4 1.4L13.4 12l5.3 5.3a1 1 0 0 1-1.4 1.4L12 13.4l-5.3 5.3a1 1 0 0 1-1.4-1.4l5.3-5.3-5.3-5.3a1 1 0 0 1 1.4-1.4Z" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto px-3.5 pb-3.5 pt-2.5 sm:px-5 sm:pb-5">
              <div className="rounded-full border border-slate-200 bg-slate-100/90 p-1">
                <div className="grid grid-cols-3 gap-0.5">
                  {ARCHIVE_SOURCE_CODES.map((code) => {
                    const active = archiveSource === code;
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => setArchiveSource(code)}
                        disabled={archiveUploading}
                        className={`rounded-full px-2.5 py-1.5 text-xs font-medium transition sm:text-[15px] ${
                          active
                            ? "bg-blue-600 text-white shadow-[0_18px_32px_-18px_rgba(37,99,235,0.9)]"
                            : "text-slate-500 hover:bg-white/80 hover:text-slate-800"
                        } ${archiveUploading ? "cursor-not-allowed opacity-70" : ""}`}
                      >
                        {code}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div
                onDragOver={handleArchiveDragOver}
                onDragEnter={handleArchiveDragOver}
                onDragLeave={handleArchiveDragLeave}
                onDrop={handleArchiveDrop}
                className={`mt-3 rounded-[18px] border-2 border-dashed px-3 py-3.5 text-center transition sm:px-5 sm:py-4 ${
                  archiveDragActive
                    ? "border-blue-400 bg-blue-50/90 shadow-[0_0_0_6px_rgba(59,130,246,0.08)]"
                    : "border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(244,247,251,0.92))]"
                }`}
              >
                <input
                  ref={archiveInputRef}
                  type="file"
                  accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  multiple
                  onChange={handleArchiveFilesChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => archiveInputRef.current?.click()}
                  className="mx-auto flex w-full max-w-[240px] flex-col items-center text-center"
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-[14px] bg-white text-slate-400 shadow-[0_14px_28px_-24px_rgba(15,23,42,0.24)]">
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                      <path fill="currentColor" d="M7 3.75A2.25 2.25 0 0 0 4.75 6v12A2.25 2.25 0 0 0 7 20.25h10A2.25 2.25 0 0 0 19.25 18V8.91c0-.6-.24-1.17-.66-1.59l-2.91-2.91a2.25 2.25 0 0 0-1.59-.66H7Zm6.25 1.8v2.7c0 .41.34.75.75.75h2.7l-3.45-3.45ZM12 11a.75.75 0 0 1 .75.75v3.69l1.47-1.47a.75.75 0 1 1 1.06 1.06l-2.75 2.75a.75.75 0 0 1-1.06 0l-2.75-2.75a.75.75 0 0 1 1.06-1.06l1.47 1.47v-3.69A.75.75 0 0 1 12 11Z" />
                    </svg>
                  </div>
                  <div className="mt-2 text-[15px] font-semibold text-slate-700">Выбрать файлы</div>
                  <div className="mt-0.5 text-xs text-slate-400">Excel .xls, .xlsx</div>
                </button>
              </div>

              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-slate-800 sm:text-[15px]">
                  Выбрано файлов: {archiveFiles.length}
                </div>
                <button
                  type="button"
                  onClick={clearArchiveSelection}
                  disabled={!archiveFiles.length}
                  className={`inline-flex items-center gap-1 text-xs font-medium transition ${
                    archiveFiles.length ? "text-blue-600 hover:text-blue-700" : "cursor-not-allowed text-slate-300"
                  }`}
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5">
                    <path fill="currentColor" d="M9 3.75A1.25 1.25 0 0 0 7.75 5v.75H5.5a.75.75 0 0 0 0 1.5h.67l.72 10.1A2.25 2.25 0 0 0 9.13 19.5h5.74a2.25 2.25 0 0 0 2.24-2.15l.72-10.1h.67a.75.75 0 0 0 0-1.5h-2.25V5A1.25 1.25 0 0 0 14.75 3.75H9Zm6.25 2h-6.5V5a.25.25 0 0 1 .25-.25h5.5a.25.25 0 0 1 .25.25v.75ZM9.72 9.47a.75.75 0 0 1 .78.72l.2 5a.75.75 0 1 1-1.5.06l-.2-5a.75.75 0 0 1 .72-.78Zm4.56 0a.75.75 0 0 1 .72.78l-.2 5a.75.75 0 1 1-1.5-.06l.2-5a.75.75 0 0 1 .78-.72Z" />
                  </svg>
                  Очистить
                </button>
              </div>

              {archiveFiles.length > 0 ? (
                <div className="mt-2.5 max-h-40 space-y-1.5 overflow-auto pr-1">
                  {archiveFiles.map((file) => (
                    <div
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      className="flex items-center gap-2.5 rounded-[16px] border border-slate-200 bg-white px-3 py-2 shadow-[0_12px_24px_-24px_rgba(15,23,42,0.18)]"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] border border-emerald-100 bg-emerald-50 text-emerald-600">
                        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                          <path fill="currentColor" d="M7 3.75A2.25 2.25 0 0 0 4.75 6v12A2.25 2.25 0 0 0 7 20.25h10A2.25 2.25 0 0 0 19.25 18V8.91c0-.6-.24-1.17-.66-1.59l-2.91-2.91a2.25 2.25 0 0 0-1.59-.66H7Zm6.25 1.8v2.7c0 .41.34.75.75.75h2.7l-3.45-3.45ZM8.9 10.35c0-.47.38-.85.85-.85h2.07l-2.87 5h1.94l1.34-2.34h1.02l-1.33 2.34h1.32c.47 0 .85.38.85.85s-.38.85-.85.85H8.82l2.86-5H9.75a.85.85 0 0 1-.85-.85Z" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-800">{file.name}</div>
                        <div className="mt-0.5 text-xs text-slate-400">{formatFileSize(file.size)}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeArchiveFile(file)}
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                        aria-label={`Убрать файл ${file.name}`}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
                          <path fill="currentColor" d="m6.7 5.3 5.3 5.3 5.3-5.3a1 1 0 1 1 1.4 1.4L13.4 12l5.3 5.3a1 1 0 0 1-1.4 1.4L12 13.4l-5.3 5.3a1 1 0 0 1-1.4-1.4l5.3-5.3-5.3-5.3a1 1 0 0 1 1.4-1.4Z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              {archiveRecentUploads.length > 0 ? (
                <div className="mt-4">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="text-[15px] font-medium text-slate-800">Недавние загрузки</div>
                    <div className="h-px flex-1 bg-slate-200" />
                  </div>
                  <div className="space-y-1.5">
                    {archiveRecentUploads.map((item) => (
                      <div
                        key={item.id || `${item.saved_name}-${item.uploaded_at}`}
                        className="flex items-center gap-2.5 rounded-[16px] border border-slate-200 bg-white px-3 py-2 shadow-[0_12px_24px_-24px_rgba(15,23,42,0.18)]"
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] border border-emerald-100 bg-emerald-50 text-emerald-600">
                          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                            <path fill="currentColor" d="M7 3.75A2.25 2.25 0 0 0 4.75 6v12A2.25 2.25 0 0 0 7 20.25h10A2.25 2.25 0 0 0 19.25 18V8.91c0-.6-.24-1.17-.66-1.59l-2.91-2.91a2.25 2.25 0 0 0-1.59-.66H7Zm6.25 1.8v2.7c0 .41.34.75.75.75h2.7l-3.45-3.45ZM8.9 10.35c0-.47.38-.85.85-.85h2.07l-2.87 5h1.94l1.34-2.34h1.02l-1.33 2.34h1.32c.47 0 .85.38.85.85s-.38.85-.85.85H8.82l2.86-5H9.75a.85.85 0 0 1-.85-.85Z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-slate-800">{item.original_name}</div>
                          <div className="mt-0.5 text-xs text-slate-400">
                            {formatFileSize(item.size)} {item.uploaded_at ? `— ${formatArchiveUploadedAt(item.uploaded_at)}` : ""}
                          </div>
                        </div>
                        <div className="text-slate-300">
                          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5">
                            <path fill="currentColor" d="M9.47 6.97a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 1 1-1.06-1.06L13.44 12 9.47 8.03a.75.75 0 0 1 0-1.06Z" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {archiveDetailUploads.length > 0 ? (
                <div className="mt-4">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="flex items-center gap-2 text-[15px] font-medium text-slate-800">
                      <span>Подробности</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{archiveDetailUploads.length}</span>
                    </div>
                    <div className="h-px flex-1 bg-slate-200" />
                  </div>
                  <div className="max-h-44 space-y-1.5 overflow-auto pr-1">
                    {archiveDetailUploads.map((item) => (
                      <div
                        key={item.id || `${item.saved_name}-${item.uploaded_at}`}
                        className="flex items-center gap-2.5 rounded-[16px] border border-slate-200 bg-white px-3 py-2 shadow-[0_12px_24px_-24px_rgba(15,23,42,0.18)]"
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] border border-emerald-100 bg-emerald-50 text-emerald-600">
                          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
                            <path fill="currentColor" d="M7 3.75A2.25 2.25 0 0 0 4.75 6v12A2.25 2.25 0 0 0 7 20.25h10A2.25 2.25 0 0 0 19.25 18V8.91c0-.6-.24-1.17-.66-1.59l-2.91-2.91a2.25 2.25 0 0 0-1.59-.66H7Zm6.25 1.8v2.7c0 .41.34.75.75.75h2.7l-3.45-3.45ZM8.9 10.35c0-.47.38-.85.85-.85h2.07l-2.87 5h1.94l1.34-2.34h1.02l-1.33 2.34h1.32c.47 0 .85.38.85.85s-.38.85-.85.85H8.82l2.86-5H9.75a.85.85 0 0 1-.85-.85Z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-slate-800">{item.original_name}</div>
                          <div className="mt-0.5 text-xs text-slate-400">
                            {formatFileSize(item.size)} {item.uploaded_at ? `— ${formatArchiveUploadedAt(item.uploaded_at)}` : ""}
                          </div>
                        </div>
                        <div className="text-slate-300">
                          <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5">
                            <path fill="currentColor" d="M9.47 6.97a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 1 1-1.06-1.06L13.44 12 9.47 8.03a.75.75 0 0 1 0-1.06Z" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {archiveMsg ? (
                <div className={`mt-3 rounded-[14px] px-3 py-2 text-xs ${
                  isArchiveDuplicateError(archiveMsg, archiveLogs)
                    ? "bg-amber-50 text-amber-700"
                    : archiveMsg.startsWith("Загружено") || archiveMsg.includes("запущен")
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-rose-50 text-rose-700"
                }`}>
                  {archiveMsg}
                </div>
              ) : null}

              {archiveLogs.length > 0 ? (
                <div className="mt-2.5 rounded-[14px] border border-rose-100 bg-rose-50 px-3 py-2.5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-rose-700">Логи парсинга</div>
                  <div className="mt-2 max-h-24 space-y-1 overflow-auto font-mono text-[11px] leading-4 text-rose-800">
                    {archiveLogs.map((line, index) => (
                      <div key={`${index}-${line}`}>{line}</div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleArchiveUpload}
                  disabled={archiveUploading}
                  className={`inline-flex min-w-[130px] items-center justify-center rounded-[16px] px-5 py-2 text-xs font-medium transition ${
                    archiveUploading
                      ? "cursor-wait bg-slate-200 text-slate-600"
                      : "bg-blue-600 text-white shadow-[0_20px_40px_-24px_rgba(37,99,235,0.95)] hover:-translate-y-0.5 hover:bg-blue-700"
                  }`}
                >
                  {archiveUploading ? `Запускаем импорт${archiveUploadDots}` : "Загрузить"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {toast && (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className={`px-4 py-3 rounded-xl shadow-xl text-sm max-w-xs
          animate-[fadeIn_0.2s_ease-out]
          ${
            toast.type === "error"
              ? "bg-red-600 text-white"
              : "bg-slate-800 text-white"
          }`}
      >
        {toast.text}
      </div>
    </div>
  )}

    </>
  );
}